import { z } from 'zod';

/** Query string for every list endpoint: ?page=1&pageSize=50&sort=-createdAt&q=text */
export const listQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
  // Field name, prefixed with "-" for descending. "cf.<key>" sorts by a custom field (A2).
  sort: z.string().regex(/^-?([a-zA-Z]+|cf\.[a-z][a-z0-9_]{0,39})$/).optional(),
  q: z.string().trim().max(200).optional(),
});
export type ListQuery = z.infer<typeof listQuery>;

export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

/** Error body for every non-2xx response. `fields` holds per-field validation messages. */
export interface ApiErrorBody {
  error: { code: string; message: string; fields?: Record<string, string> };
}

export const id = z.string().min(1).max(64);
export const optionalId = id.nullish().transform((v) => v ?? null);
/** Empty strings from forms become null, so optional text columns stay clean. */
export const optionalText = (max = 2000) =>
  z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((v) => (v ? v : null));
export const requiredText = (max = 200) => z.string().trim().min(1, 'Required').max(max);
export const email = z.string().trim().toLowerCase().email('Enter a valid email address').max(254);
export const optionalEmail = z
  .string()
  .trim()
  .toLowerCase()
  .max(254)
  .nullish()
  .transform((v) => (v ? v : null))
  .refine((v) => v === null || z.string().email().safeParse(v).success, 'Enter a valid email address');
/** Dates travel as YYYY-MM-DD for @db.Date columns. */
export const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD');
export const optionalIsoDate = isoDate.nullish().transform((v) => v ?? null);
export const isoDateTime = z.string().datetime({ offset: true });
/** Money and quantities travel as strings to keep Decimal precision. */
export const money = z.union([z.string(), z.number()]).transform((v) => String(v)).refine((v) => /^-?\d+(\.\d{1,2})?$/.test(v), 'Enter an amount like 12.50');
export const quantity = z.union([z.string(), z.number()]).transform((v) => String(v)).refine((v) => /^-?\d+(\.\d{1,2})?$/.test(v), 'Enter a number');
export const optionalMoney = money.nullish().transform((v) => v ?? null);

/**
 * Update (PATCH) schema from a create schema: every field optional and NO defaults.
 * zod 4's `.partial()` keeps `.default()`, so a PATCH sending one field would silently reset
 * the others to their defaults. Always use this for updates instead of `.partial()`.
 */
export function updateOf<S extends z.ZodObject>(schema: S): z.ZodType<Partial<z.output<S>>, Partial<z.input<S>>> {
  const shape: Record<string, z.ZodType> = {};
  for (const [key, field] of Object.entries(schema.shape as Record<string, z.ZodType>)) {
    shape[key] = (field instanceof z.ZodDefault ? (field.unwrap() as z.ZodType) : field).optional();
  }
  return z.object(shape) as unknown as z.ZodType<Partial<z.output<S>>, Partial<z.input<S>>>;
}
